/**
 * Certification Slice
 * 
 * Redux state management for EX7 (Process Owner Certification)
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetReduxState } from '../actions';
import type {
  Quarter,
  ProcessOwner,
  CertificationQuarterData,
  ChecklistItem,
  CertificationDocument,
  ProcessCertification,
} from '../../services/certificationService';
import { getCurrentQuarter, getCurrentFiscalYear } from '../../services/certificationService';

// ========================================
// State Interface
// ========================================

interface CertificationState {
  // Context Selection
  selectedQuarter: Quarter;
  selectedProcessOwnerId: string | null;
  fiscalYear: number;
  
  // Data - Process Owners
  processOwners: ProcessOwner[];
  lastFetchedReportId: string | null; // Track which report the data belongs to
  
  // Data - Processes by Owner (Admin Dashboard Tab 1)
  processesByOwnerId: Record<string, ProcessCertification[]>;
  fetchedOwnerIds: string[]; // Track which owners have been fetched
  
  // Data - Quarter Details
  quarterData: CertificationQuarterData | null;
  checklistItems: ChecklistItem[];
  documents: CertificationDocument[];
  processCertifications: ProcessCertification[];
  
  // Draft State (for Admin checklist configuration)
  draftChecklistItems: ChecklistItem[];
  hasUnsavedChanges: boolean;
  
  // Remind Mode (Admin Tab 1)
  isRemindMode: boolean;
  selectedProcessIdsForRemind: string[];
  
  // Checklist saved state (for PO - enables certification button)
  isChecklistSaved: boolean;
}

// ========================================
// Initial State
// ========================================

const initialState: CertificationState = {
  // Context Selection
  selectedQuarter: getCurrentQuarter(),
  selectedProcessOwnerId: null,
  fiscalYear: getCurrentFiscalYear(),
  
  // Data - Process Owners
  processOwners: [],
  lastFetchedReportId: null,
  
  // Data - Processes by Owner
  processesByOwnerId: {},
  fetchedOwnerIds: [],
  
  // Data - Quarter Details
  quarterData: null,
  checklistItems: [],
  documents: [],
  processCertifications: [],
  
  // Draft State
  draftChecklistItems: [],
  hasUnsavedChanges: false,
  
  // Remind Mode
  isRemindMode: false,
  selectedProcessIdsForRemind: [],
  
  // Checklist saved state
  isChecklistSaved: false,
};

// ========================================
// Slice
// ========================================

const certificationSlice = createSlice({
  name: 'certification',
  initialState,
  reducers: {
    // ========== Context Selection ==========
    
    setSelectedQuarter: (state, action: PayloadAction<Quarter>) => {
      state.selectedQuarter = action.payload;
      // Reset checklist saved state when quarter changes
      state.isChecklistSaved = false;
    },
    
    setSelectedProcessOwner: (state, action: PayloadAction<string | null>) => {
      state.selectedProcessOwnerId = action.payload;
      // Reset checklist saved state when owner changes
      state.isChecklistSaved = false;
    },
    
    setFiscalYear: (state, action: PayloadAction<number>) => {
      state.fiscalYear = action.payload;
    },
    
    // ========== Data Loading ==========
    
    setProcessOwners: (state, action: PayloadAction<{ owners: ProcessOwner[]; reportId: string }>) => {
      state.processOwners = action.payload.owners;
      state.lastFetchedReportId = action.payload.reportId;
      // Auto-select first owner if none selected
      if (!state.selectedProcessOwnerId && action.payload.owners.length > 0) {
        state.selectedProcessOwnerId = action.payload.owners[0].id;
      }
    },
    
    setProcessesForOwner: (state, action: PayloadAction<{ ownerId: string; processes: ProcessCertification[] }>) => {
      const { ownerId, processes } = action.payload;
      state.processesByOwnerId[ownerId] = processes;
      // Track that this owner has been fetched
      if (!state.fetchedOwnerIds.includes(ownerId)) {
        state.fetchedOwnerIds.push(ownerId);
      }
    },
    
    clearAllProcesses: (state) => {
      state.processesByOwnerId = {};
      state.fetchedOwnerIds = [];
    },
    
    setQuarterData: (state, action: PayloadAction<CertificationQuarterData | null>) => {
      state.quarterData = action.payload;
    },
    
    setChecklistItems: (state, action: PayloadAction<ChecklistItem[]>) => {
      state.checklistItems = action.payload;
      // Initialize draft items as copy of actual items
      state.draftChecklistItems = action.payload.map(item => ({ ...item }));
      state.hasUnsavedChanges = false;
    },
    
    setDocuments: (state, action: PayloadAction<CertificationDocument[]>) => {
      state.documents = action.payload;
    },
    
    setProcessCertifications: (state, action: PayloadAction<ProcessCertification[]>) => {
      state.processCertifications = action.payload;
    },
    
    // ========== Remind Mode (Admin Tab 1) ==========
    
    enterRemindMode: (state) => {
      state.isRemindMode = true;
      state.selectedProcessIdsForRemind = [];
    },
    
    exitRemindMode: (state) => {
      state.isRemindMode = false;
      state.selectedProcessIdsForRemind = [];
    },
    
    toggleProcessForRemind: (state, action: PayloadAction<string>) => {
      const processId = action.payload;
      const index = state.selectedProcessIdsForRemind.indexOf(processId);
      if (index >= 0) {
        state.selectedProcessIdsForRemind.splice(index, 1);
      } else {
        state.selectedProcessIdsForRemind.push(processId);
      }
    },
    
    selectAllProcessesForRemind: (state, action: PayloadAction<string[]>) => {
      state.selectedProcessIdsForRemind = action.payload;
    },
    
    clearRemindSelections: (state) => {
      state.selectedProcessIdsForRemind = [];
    },
    
    // ========== Draft Checklist (Admin Tab 4) ==========
    
    addDraftQuestion: (state, action: PayloadAction<string>) => {
      const newItem: ChecklistItem = {
        checklistLineItemId: `draft-${Date.now()}`,
        question: action.payload,
        significantChange: null,
        response: '',
        certificationQuarterId: state.quarterData?.certificationQuarterId || '',
        isNew: true,
      };
      state.draftChecklistItems.push(newItem);
      state.hasUnsavedChanges = true;
    },
    
    insertDraftQuestion: (state, action: PayloadAction<{ question: string; atIndex: number }>) => {
      const newItem: ChecklistItem = {
        checklistLineItemId: `draft-${Date.now()}`,
        question: action.payload.question,
        significantChange: null,
        response: '',
        certificationQuarterId: state.quarterData?.certificationQuarterId || '',
        isNew: true,
      };
      state.draftChecklistItems.splice(action.payload.atIndex, 0, newItem);
      state.hasUnsavedChanges = true;
    },
    
    updateDraftQuestion: (state, action: PayloadAction<{ id: string; question: string }>) => {
      const item = state.draftChecklistItems.find(i => i.checklistLineItemId === action.payload.id);
      if (item) {
        item.question = action.payload.question;
        item.isModified = true;
        state.hasUnsavedChanges = true;
      }
    },
    
    deleteDraftQuestion: (state, action: PayloadAction<string>) => {
      const item = state.draftChecklistItems.find(i => i.checklistLineItemId === action.payload);
      if (item) {
        if (item.isNew) {
          // Remove new items completely
          state.draftChecklistItems = state.draftChecklistItems.filter(
            i => i.checklistLineItemId !== action.payload
          );
        } else {
          // Mark existing items as deleted
          item.isDeleted = true;
        }
        state.hasUnsavedChanges = true;
      }
    },
    
    resetDraftChecklist: (state) => {
      state.draftChecklistItems = state.checklistItems.map(item => ({ ...item }));
      state.hasUnsavedChanges = false;
    },
    
    commitDraftChecklist: (state) => {
      // After successful API call, update actual items from draft
      const committedItems = state.draftChecklistItems
        .filter(item => !item.isDeleted)
        .map(item => ({
          ...item,
          isNew: false,
          isDeleted: false,
          isModified: false,
        }));
      state.checklistItems = committedItems;
      // Also update draftChecklistItems to match (remove deleted items)
      state.draftChecklistItems = committedItems.map(item => ({ ...item }));
      state.hasUnsavedChanges = false;
    },
    
    // ========== PO Checklist Responses (Tab 3) ==========
    
    updateChecklistResponse: (
      state,
      action: PayloadAction<{
        id: string;
        significantChange?: ChecklistItem['significantChange'];
        response?: string;
      }>
    ) => {
      const item = state.draftChecklistItems.find(
        i => i.checklistLineItemId === action.payload.id
      );
      if (item) {
        if (action.payload.significantChange !== undefined) {
          item.significantChange = action.payload.significantChange;
        }
        if (action.payload.response !== undefined) {
          item.response = action.payload.response;
        }
        item.isModified = true;
        state.hasUnsavedChanges = true;
      }
    },
    
    setChecklistSaved: (state, action: PayloadAction<boolean>) => {
      state.isChecklistSaved = action.payload;
    },
    
    // ========== Certification Update ==========
    
    markProcessesCertified: (state, action: PayloadAction<string[]>) => {
      const processIds = action.payload;
      const currentQuarter = state.selectedQuarter;
      const today = new Date().toISOString().split('T')[0];
      
      // Update process certifications
      state.processCertifications.forEach(proc => {
        if (processIds.includes(proc.processId)) {
          switch (currentQuarter) {
            case 1:
              proc.q1SignOffDate = today;
              break;
            case 2:
              proc.q2SignOffDate = today;
              break;
            case 3:
              proc.q3SignOffDate = today;
              break;
            case 4:
              proc.q4SignOffDate = today;
              break;
          }
        }
      });
      
      // Update quarter data if loaded
      if (state.quarterData) {
        state.quarterData.cycles.forEach(cycle => {
          cycle.processes.forEach(proc => {
            if (processIds.includes(proc.processId)) {
              proc.isCertified = true;
            }
          });
        });
      }
    },
    
    // ========== Clear State ==========
    
    clearCertificationData: (state) => {
      state.quarterData = null;
      state.checklistItems = [];
      state.draftChecklistItems = [];
      state.documents = [];
      state.processCertifications = [];
      state.hasUnsavedChanges = false;
      state.isChecklistSaved = false;
    },
    
    // Clear only checklist data (used when quarter changes)
    clearChecklistData: (state) => {
      state.checklistItems = [];
      state.draftChecklistItems = [];
      state.hasUnsavedChanges = false;
      state.isChecklistSaved = false;
    },
    
    // Reset all cached data when leaving the page
    // This forces a fresh fetch when returning to the page
    // Keeps selectedQuarter and selectedProcessOwnerId for UX continuity
    resetPageCache: (state) => {
      // Clear process owners cache
      state.processOwners = [];
      state.lastFetchedReportId = null;
      
      // Clear processes cache
      state.processesByOwnerId = {};
      state.fetchedOwnerIds = [];
      
      // Clear quarter data
      state.quarterData = null;
      state.checklistItems = [];
      state.draftChecklistItems = [];
      state.documents = [];
      state.processCertifications = [];
      
      // Reset UI state
      state.hasUnsavedChanges = false;
      state.isChecklistSaved = false;
      state.isRemindMode = false;
      state.selectedProcessIdsForRemind = [];
    },
  },
  
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

// ========================================
// Export Actions
// ========================================

export const {
  // Context Selection
  setSelectedQuarter,
  setSelectedProcessOwner,
  setFiscalYear,
  
  // Data Loading
  setProcessOwners,
  setProcessesForOwner,
  clearAllProcesses,
  setQuarterData,
  setChecklistItems,
  setDocuments,
  setProcessCertifications,
  
  // Remind Mode
  enterRemindMode,
  exitRemindMode,
  toggleProcessForRemind,
  selectAllProcessesForRemind,
  clearRemindSelections,
  
  // Draft Checklist
  addDraftQuestion,
  insertDraftQuestion,
  updateDraftQuestion,
  deleteDraftQuestion,
  resetDraftChecklist,
  commitDraftChecklist,
  
  // PO Checklist Responses
  updateChecklistResponse,
  setChecklistSaved,
  
  // Certification Update
  markProcessesCertified,
  
  // Clear
  clearCertificationData,
  clearChecklistData,
  resetPageCache,
} = certificationSlice.actions;

// ========================================
// Export Reducer
// ========================================

export default certificationSlice.reducer;

